import React, { useEffect, useRef, useState } from 'react';
import { Colors, Duration, Easing, ZIndex } from '@common/tokens';
import { keysOf, PanelLayer, reconcileExitingLayers } from './_panel-layers';

// Cushion past the CSS slide duration before the fallback unmount fires, so it
// never races a real `animationend`.
const ANIMATION_END_GRACE_MS = 50;

// Fallback unmount delay for an exiting layer, matched to the slide duration.
// `animationend` removes it sooner; this guarantees cleanup when animations
// don't run (e.g. `prefers-reduced-motion`).
const SLIDE_FALLBACK_MS =
  parseInt(Duration.normal, 10) + ANIMATION_END_GRACE_MS;

// React 18 has no first-class `inert` prop; spread the attribute (empty string =
// present) the same way SideShell hides its collapsed side content.
const inertProps = (hidden: boolean) => (hidden ? { inert: '' } : {});

/**
 * Renders the SideShell main area as a navigation stack: `children` is the
 * persistent base layer and each `layers` entry is a panel that slides in from
 * the right over the one beneath. Lower layers stay mounted but hidden (`inert`)
 * so their state is preserved; popping the top slides it back out to the right
 * to reveal the layer below.
 *
 * The slide is pure CSS: a mounted layer animates in on mount, an exiting layer
 * is kept mounted (in `exitingLayers`) until its slide-out animation ends.
 *
 * Internal to SideShell — not exported from the package barrel.
 */
export const PanelStack = ({
  children,
  layers,
}: {
  children: React.ReactNode;
  layers: PanelLayer[];
}) => {
  // Popped layers kept mounted while they animate out (node captured on removal).
  const [exitingLayers, setExitingLayers] = useState<PanelLayer[]>([]);

  // Snapshot of the previous stack (with nodes), refreshed on each structural
  // change so a popped layer can still be rendered while it slides out.
  const prevLayersRef = useRef(layers);

  const exitTimersRef = useRef(
    new Map<string, ReturnType<typeof setTimeout>>()
  );

  const clearExitTimer = (key: string) => {
    const timer = exitTimersRef.current.get(key);
    if (timer !== undefined) {
      clearTimeout(timer);
      exitTimersRef.current.delete(key);
    }
  };

  const dropExiting = (key: string) => {
    clearExitTimer(key);
    setExitingLayers((prev) => prev.filter((layer) => layer.key !== key));
  };

  const stackSignature = keysOf(layers).join(' ');

  // On a structural change, start sliding out any popped key and stop tracking
  // any key that came back. Keyed on `stackSignature` so it ignores the parent's
  // array identity (the `panels` map is often rebuilt each render).
  useEffect(() => {
    const nextKeys = keysOf(layers);
    const removed = prevLayersRef.current.filter(
      (layer) => !nextKeys.includes(layer.key)
    );

    setExitingLayers((prev) => reconcileExitingLayers(prev, removed, nextKeys));

    nextKeys.forEach(clearExitTimer);
    removed.forEach((layer) => {
      clearExitTimer(layer.key);
      exitTimersRef.current.set(
        layer.key,
        setTimeout(() => dropExiting(layer.key), SLIDE_FALLBACK_MS)
      );
    });

    prevLayersRef.current = layers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackSignature]);

  // Clear any pending exit timers on unmount.
  const timers = exitTimersRef.current;
  useEffect(() => () => timers.forEach(clearTimeout), [timers]);

  const topIndex = layers.length - 1;
  // The base is covered (and made inert) whenever a panel is on top.
  const baseHidden = layers.length > 0;
  const topKey = topIndex >= 0 ? layers[topIndex].key : null;

  // Follow the navigation with focus: when the top changes, move focus to the
  // now-topmost layer (or the base when the stack empties) so keyboard / screen
  // reader users aren't dropped onto <body> when a panel opens or closes. The
  // ref tracks whichever layer is currently interactive.
  const activeLayerRef = useRef<HTMLDivElement>(null);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    // `preventScroll` is essential: an opening panel starts off-screen at
    // translateX(100%), and a plain focus() would scroll it into view and cut
    // its slide-in animation short.
    activeLayerRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topKey]);

  return (
    <div className="wk-side-shell-panel-stack">
      <div
        className="wk-side-shell-panel-stack__layer wk-side-shell-panel-stack__layer--base"
        ref={baseHidden ? undefined : activeLayerRef}
        tabIndex={baseHidden ? undefined : -1}
        aria-hidden={baseHidden}
        {...inertProps(baseHidden)}
      >
        {children}
      </div>

      {layers.map((layer, index) => {
        const hidden = index !== topIndex;

        return (
          <div
            key={layer.key}
            className="wk-side-shell-panel-stack__layer"
            ref={hidden ? undefined : activeLayerRef}
            tabIndex={hidden ? undefined : -1}
            style={{ zIndex: ZIndex.base + index + 1 }}
            aria-hidden={hidden}
            {...inertProps(hidden)}
          >
            {layer.node}
          </div>
        );
      })}

      {exitingLayers.map((layer, index) => (
        <div
          key={`exiting-${layer.key}`}
          className="wk-side-shell-panel-stack__layer wk-side-shell-panel-stack__layer--exiting"
          style={{ zIndex: ZIndex.base + layers.length + index + 1 }}
          aria-hidden
          {...inertProps(true)}
          onAnimationEnd={() => dropExiting(layer.key)}
        >
          {layer.node}
        </div>
      ))}

      <style jsx>{`
        .wk-side-shell-panel-stack {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
        }
        .wk-side-shell-panel-stack__layer {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          background: ${Colors.white};
          animation: wk-side-shell-panel-slide-in ${Duration.normal}
            ${Easing.ease};
        }
        /* The layer only receives focus programmatically (tabIndex -1) to seed
           the reading position; suppress the container outline so the ring
           shows on the real controls inside, not the whole panel. */
        .wk-side-shell-panel-stack__layer:focus {
          outline: none;
        }
        /* Base content sits at the bottom of the stack, never animates, and is
           transparent so the side section's own background shows through (the
           opaque white is only needed on panels that slide over it). */
        .wk-side-shell-panel-stack__layer--base {
          z-index: ${ZIndex.base};
          animation: none;
          background: transparent;
        }
        .wk-side-shell-panel-stack__layer--exiting {
          animation: wk-side-shell-panel-slide-out ${Duration.normal}
            ${Easing.ease} forwards;
        }
        @keyframes wk-side-shell-panel-slide-in {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        @keyframes wk-side-shell-panel-slide-out {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(100%);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .wk-side-shell-panel-stack__layer {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
};
